import { useQueryClient } from "@tanstack/react-query"
import {
	PROJECT_ACCESS_EVENT_NAMES,
	type RealtimeMessage,
} from "@zplanem/responses"
import { useCallback } from "react"
import { queryKeys } from "../../lib/query-keys"
import { usePerUserChannelEvents } from "./use-per-user-channel-event"

/**
 * Mounted app-wide (ZPL-282): when the signed-in user is added to or removed
 * from a project, refresh their cached project views. On removal the detail
 * refetch surfaces the 404/forbidden state instead of leaving a live view of
 * a project they can no longer access. Mirrors useDocumentAccessRealtime —
 * per-user channel, because access is per-user.
 */
export function useProjectAccessRealtime(userId: string | undefined): void {
	const queryClient = useQueryClient()
	const handler = useCallback(
		(data: unknown) => {
			const msg = data as RealtimeMessage
			if (msg.event !== "project.access.changed") return
			void queryClient.invalidateQueries({
				queryKey: queryKeys.projects(msg.payload.propertyId),
			})
			void queryClient.invalidateQueries({
				queryKey: queryKeys.project(
					msg.payload.propertyId,
					msg.payload.projectId,
				),
			})
		},
		[queryClient],
	)
	usePerUserChannelEvents(userId, PROJECT_ACCESS_EVENT_NAMES, handler)
}
